import type { AnalyticsDateRange } from '@/components/AnalyticsDateRangeFilter';

/**
 * The time window the admin analytics endpoints query for a date range.
 * Mirrors the backend's resolveAnalyticsDateRange
 * (wcc4-backend src/modules/admin/utils/analytics-date-range.util.ts): whole
 * IST days (UTC+05:30, no DST), both ends inclusive. IST is computed with the
 * fixed offset, so the result doesn't depend on the browser's time zone.
 */
const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;
const ALL_TIME_START = '2026-01-01';

export interface AnalyticsWindow {
  start: Date;
  /** Inclusive: the last millisecond of its IST day. */
  end: Date;
}

/** YYYY-MM-DD of the IST day containing the instant `ms`. */
const istDate = (ms: number): string => new Date(ms + IST_OFFSET_MS).toISOString().slice(0, 10);

const istDayStart = (yyyyMmDd: string): Date => new Date(`${yyyyMmDd}T00:00:00+05:30`);
const istDayEnd = (yyyyMmDd: string): Date => new Date(`${yyyyMmDd}T23:59:59.999+05:30`);

/**
 * The window a range covers when fetched at `asOf` (ms). With D = the IST
 * date at `asOf`: today is D; yesterday is D-1; last_N_days is D-N to D
 * (N+1 days, today included); all is 1 Jan 2026 to D; custom is the picked
 * calendar dates as IST days. Null for a custom range without both dates.
 */
export function analyticsWindow(
  range: AnalyticsDateRange,
  asOf: number = Date.now()
): AnalyticsWindow | null {
  const todayStart = istDayStart(istDate(asOf)).getTime();
  const todayEnd = new Date(todayStart + DAY_MS - 1);
  const daysBack = (n: number) => new Date(todayStart - n * DAY_MS);

  switch (range.period) {
    case 'today':
      return { start: new Date(todayStart), end: todayEnd };
    case 'yesterday':
      return { start: daysBack(1), end: new Date(todayStart - 1) };
    case 'last_7_days':
      return { start: daysBack(7), end: todayEnd };
    case 'last_30_days':
      return { start: daysBack(30), end: todayEnd };
    case 'last_90_days':
      return { start: daysBack(90), end: todayEnd };
    case 'all':
      return { start: istDayStart(ALL_TIME_START), end: todayEnd };
    case 'custom': {
      const [from, to] = range.customRange ?? [];
      if (!from || !to) return null;
      // The pages send these same calendar dates as YYYY-MM-DD, which the
      // backend reads as IST days.
      return {
        start: istDayStart(from.format('YYYY-MM-DD')),
        end: istDayEnd(to.format('YYYY-MM-DD')),
      };
    }
    default:
      return null;
  }
}

const pad = (n: number) => String(n).padStart(2, '0');

/** DD/MM/YYYY HH:mm of an instant, in IST (24-hour clock). */
export function formatIstDateTime(date: Date): string {
  const ist = new Date(date.getTime() + IST_OFFSET_MS);
  return `${pad(ist.getUTCDate())}/${pad(ist.getUTCMonth() + 1)}/${ist.getUTCFullYear()} ${pad(ist.getUTCHours())}:${pad(ist.getUTCMinutes())}`;
}

/** e.g. "09/10/2026 00:00 – 09/10/2026 23:59 IST"; null when there is no window. */
export function analyticsWindowLabel(range: AnalyticsDateRange, asOf?: number): string | null {
  const covered = analyticsWindow(range, asOf);
  return covered
    ? `${formatIstDateTime(covered.start)} – ${formatIstDateTime(covered.end)} IST`
    : null;
}
